import React, { useCallback, useEffect, useMemo, useState } from 'react';
import VideoService from '../../../../service/VideoService';
import { timeStampToMs } from '../../../../utils/TimeUtil';
import format from 'date-fns/format';
import SubtitlesRightSidebar from '../SubtitlesRightSidebar/SubtitlesRightSidebar';
import { useUIContext } from '../../../../context/UIContext';
import styles from './SubtitlesEditorContent.module.scss';
import { parseSync, stringifySync } from 'subtitle';
import EditSubtitleButtons from '../EditSubtitleButtons/EditSubtitleButtons';
import SaveSubtitlesModal from '../SaveSubtitlesModal/SaveSubtitlesModal';
import { useHistory } from 'react-router-dom';
import CmsAPI from '../../../../../common/apis/CmsAPI';
import { Auth } from 'aws-amplify';
import SubtitlesPlayer from '../../components/SubtitlesPlayer/SubtitlesPlayer';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import supportedLanguages from '../../../../../common/constants/supportedLanguages.json';
import ResourceHelper from '../../../../utils/ResourceHelper';
import { useUserContext } from '../../../../context/UserContext';
import { EDITOR_MODE, MISSING_MODE, TIME_CORRECTION_STEP, VOICEOVER_COVERAGE_TYPES } from '../../const';
import { ToastNotification } from '../../../../components/Toast/Toast';
import { withPermissionCheck } from '../../../../hocs/withPermissionCheck';
import useSubtitlesActions from '../../../../hooks/useSubtitlesActions';
import { v4 as uuidv4 } from 'uuid';
import { getCurrentActions } from '../../../../utils/CallToActionHelper';
import { useCallToActionContext } from '../../../../context/CallToActionContext';
import VormatsAPI from '../../../../../common/apis/VormatsAPI';
import { checkForFullCoverage, getItemOverlappedBy } from '../../utils';
import { CLIPS_TYPE, VOICEOVER_TYPE } from '../../../../constants/clipsConstants';
import VoiceSelectionModal from '../VoiceSelectionModal/VoiceSelectionModal';
import Modal from '../../../../../engage/components/Modal';
import { CustomButton } from '../../../../components/Button/Button';

/**
 * The component gets all scenes subtitles, and joins them into one array adding the consecutive timestamps
 * Videos intro duration is also taken into account
 *
 * Backend accepts FULL scene srt to be sent, even if scene is trimmed, that's why subtitles array needs to
 * always request and parse trimmed and not trimmed srt and join them (only items with field 'trimmedData'
 * are visible in editor for user).
 *
 */
const { TRANSCRIPTION, TRANSLATION } = MISSING_MODE;
const ENHANCE_ESTIMATION_TIME = "10-30";
const TRANSLATION_ESTIMATION_TIME = "5-20";

const TRANSCRIBE_PARALLEL_VIDEOS = 3;
const TRANSCRIBE_ESTIMATION_FACTOR = 0.55;

const EMPTY_SUBTITLE_LENGTH = 5000;

const SubtitlesEditorContent = ({ videoId, initVideoDetails, initialVideoConfig: videoConfig }) => {
  const { t } = useTranslation();

  const [video, setVideo] = useState(null);
  const [loading, setLoading] = useState(transcribeLoadingText);
  const [subtitles, setSubtitles] = useState({});
  const [subtitlesLanguage, setSubtitlesLanguage] = useState({});
  const [activeSubtitle, setActiveSubtitle] = useState({});
  const [seekToSubtitleValue, setSeekToSubtitleValue] = useState(null);
  const [hasChanged, setHasChanged] = useState(false);
  const [translateConfirm, setTranslateConfirm] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState({});
  const [mode, setMode] = useState(EDITOR_MODE.GENERATE_SUBTITLES_MODE);
  const [appConfigLanguages, setAppConfigLanguages] = useState([]);
  const [playedSeconds, setPlayedSeconds] = useState(0);
  const [init, setInit] = useState(false);
  const [error, setError] = useState(false);
  const [estimatedTime, setEstimatedTime] = useState(0);
  const [changedItems, setChangedItems] = useState({});
  const [isMissing, setIsMissing] = useState({ [TRANSCRIPTION]: false, [TRANSLATION]: false });
  const [videoChapters, setVideoChapters] = useState([]);
  const [listMode, setListMode] = useState(null);
  const [showVoiceModal, setShowVoiceModal] = useState(false);
  const [showEnhanceConfirmModal, setShowEnhanceConfirmModal] = useState(false);

  const transcribeLoadingText = {
    header: t('subtitles.editor.loading.header'),
    textLines: [
      t('subtitles.editor.loading.text_second_string'),
      t('subtitles.editor.loading.text_third_string')
    ],
    includeCancelButton: true
  };

  const translateLoadingText = {
    header: t('subtitles.editor.loading_translation.header'),
    textLines: [
      t('subtitles.editor.loading.text_do_not_close_page'),
      t('subtitles.editor.loading.text_static_estimated_time', { time: TRANSLATION_ESTIMATION_TIME })
    ],
    includeCancelButton: false
  };

  const enhanceLoadingText = {
    header: t('subtitles.editor.loading_enhance.header'),
    textLines: [
      t('subtitles.editor.loading.text_do_not_close_page'),
      t('subtitles.editor.loading.text_static_estimated_time', { time: ENHANCE_ESTIMATION_TIME })
    ],
    includeCancelButton: false
  };

  const { updateCallActions, updateInitialCallActions } = useCallToActionContext();

  const introDuration = useMemo(() => (video?.hasIntro && video?.introMeta ? video.introMeta.duration : 0), [video]);

  const selectedLanguageSubtitles = useMemo(() => subtitles[currentLanguage.id] ?? [], [subtitles, currentLanguage]);

  const availableLanguages = useMemo(() => {
    const languages = Object.keys(subtitles);
    return languages.map(item => {
      const language = supportedLanguages.find(supported => supported.id === item);
      for (const resource of Object.values(subtitles[item])) {
        if (resource && resource.some(subtitleEntry => subtitleEntry.enhanced)) {
          return { ...language, name: `${language.name} (${t('subtitles.editor.improved_label')})` };
        }
      }
      return language;
    });
  }, [subtitles, supportedLanguages]);

  // Extract the filter for the current subtitles language into a memo
  const subtitlesLanguageWithEnhanced = useCallback((language) =>
    availableLanguages.find(item => item.id === language.id) ?? language,
    [availableLanguages, subtitlesLanguage, currentLanguage]
  );

  const mediaMatch = window.matchMedia('(max-width: 900px)');
  const [isMobileView] = useState(mediaMatch.matches);

  const { appConfig } = useUserContext();
  const { miniSidebar } = useUIContext();

  const history = useHistory();

  const scenesLimits = useMemo(
    () =>
      video?.scenes
        ?.sort((a, b) => a.sort - b.sort)
        ?.map((scene, index, scenes) => {
          const durationBefore = index
            ? scenes
              .slice(0, index)
              .map(({ trimmedDuration, playbackRate }) => trimmedDuration / playbackRate)
              .reduce((prev, current) => prev + current, introDuration)
            : introDuration;
          return {
            sceneId: scene.id,
            start: durationBefore,
            end: durationBefore + scene.trimmedDuration / scene.playbackRate,
            playbackRate: scene.playbackRate,
          };
        }),
    [video, introDuration]
  );

  const { newGeneralAction, undoLastAction, redoLastAction, clearStorage, controlsDisabled } = useSubtitlesActions({
    updateSubtitles: setSubtitles,
    changeLanguage: setCurrentLanguage,
    seekToSubtitle: setSeekToSubtitleValue,
  });

  const handleUpdateSubtitles = useCallback(
    ({ sceneId = null, segmentId = null, id = null, key, value }) => {
      const mode = sceneId ? CLIPS_TYPE : VOICEOVER_TYPE;
      const keyId = sceneId ?? segmentId;
      const keyName = sceneId ? 'sceneId' : 'segmentId';
      const sceneOrSegmentInfo = selectedLanguageSubtitles[mode].find(item => item[keyName] === keyId);
      const itemSubtitlesToEdit = structuredClone(sceneOrSegmentInfo?.subtitles);
      const filteredByTrimmedData = itemSubtitlesToEdit?.filter(item => item.hasOwnProperty('trimmedData'));
      const isSubtitlesEmpty = filteredByTrimmedData.length === 0;
      const subtitleIndex =
        id === null
          ? itemSubtitlesToEdit.findIndex(item => item?.key === filteredByTrimmedData[0]?.key)
          : itemSubtitlesToEdit.findIndex(item => item?.key === id);

      const isIncorrectSubtitleIndex = subtitleIndex < 0;

      if (isIncorrectSubtitleIndex && !isSubtitlesEmpty) {
        return;
      }

      const actionToBuffer = {
        currentLanguage: currentLanguage,
        prevSceneSubtitles: itemSubtitlesToEdit,
        undoStartValue: 0,
        redoStartValue: 0,
        [keyName]: keyId,
      };
      if (!isSubtitlesEmpty) {
        setHasChanged(true);
        key !== 'add' && updateChangedItems({ [keyName]: keyId, language: currentLanguage.id });
      }
      let updatedSubtitles = [];
      switch (key) {
        case 'start': {
          // The starting value of the subtitle with which the action will be performed
          // Used for undo/redo to select the operated subtitle
          let undoStartValue;
          let redoStartValue;
          if (filteredByTrimmedData.findIndex(item => item.key === id) === 0) {
            updatedSubtitles = itemSubtitlesToEdit.map(item => {
              if (id === item.key) {
                const updatedValueData = item.data;
                const updatedValueTrimmedData = { ...item.trimmedData };
                const updatedStart = updatedValueData.start + (value - updatedValueTrimmedData.start) * (item?.playbackRate ?? 1);
                updatedValueData.start = Math.max(0, updatedStart);
                updatedValueTrimmedData.start = value;
                undoStartValue = item.trimmedData.start;
                redoStartValue = value;
                return { ...item, trimmedData: updatedValueTrimmedData, data: updatedValueData };
              } else {
                return item;
              }
            });
          } else {
            const previousSubtitle = itemSubtitlesToEdit[subtitleIndex - 1];
            if (previousSubtitle?.trimmedData?.end > value) {
              previousSubtitle.data.end += (value - previousSubtitle.trimmedData.end) * (previousSubtitle?.playbackRate ?? 1);
              previousSubtitle.trimmedData.end = value;
            }

            const editingSubtitleItem = structuredClone(itemSubtitlesToEdit[subtitleIndex]);
            const updatedStart =
              editingSubtitleItem.data.start + (value - editingSubtitleItem.trimmedData.start) * (editingSubtitleItem?.playbackRate ?? 1);
            editingSubtitleItem.data.start = Math.max(0, updatedStart);
            undoStartValue = editingSubtitleItem.trimmedData.start;
            editingSubtitleItem.trimmedData.start = value;
            redoStartValue = value;

            updatedSubtitles = structuredClone(itemSubtitlesToEdit).map((subtitle, subIndex) => {
              if (subIndex === subtitleIndex) {
                return editingSubtitleItem;
              } else if (subIndex === subtitleIndex - 1) {
                return previousSubtitle;
              } else {
                return subtitle;
              }
            });
          }
          actionToBuffer.undoStartValue = undoStartValue;
          actionToBuffer.redoStartValue = redoStartValue;
          break;
        }
        case 'end': {
          let startValue;

          const followingSubtitles = structuredClone(itemSubtitlesToEdit)
            .slice(subtitleIndex + 1)
            .filter(item => item.hasOwnProperty('trimmedData'));
          if (followingSubtitles.length === 0) {
            updatedSubtitles = itemSubtitlesToEdit.map((item, itemIndex) => {
              if (subtitleIndex === itemIndex) {
                const sortedScenes = video.scenes.sort((a, b) => a.sort - b.sort);
                const scenesBefore = sortedScenes.slice(0, video.scenes.length - 1);
                let lastSceneShift = 0;
                if (scenesBefore.length > 0) {
                  lastSceneShift += scenesBefore
                    .map(scene => scene.trimmedDuration / (scene?.playbackRate ?? 1))
                    .reduce((acc, value) => acc + value);
                }
                const updatedValueData = { ...item.data };
                const updatedValueTrimmedData = { ...item.trimmedData };
                updatedValueData.end += (value - updatedValueTrimmedData.end) * (item?.playbackRate ?? 1);
                updatedValueTrimmedData.end = value;
                startValue = updatedValueTrimmedData.start;
                return { ...item, trimmedData: updatedValueTrimmedData, data: updatedValueData };
              } else {
                return item;
              }
            });
          } else {
            const nextSubtitle = structuredClone(followingSubtitles[0]);
            if (nextSubtitle.trimmedData?.start < value) {
              nextSubtitle.data.start += (value - nextSubtitle.trimmedData.start) * (nextSubtitle?.playbackRate ?? 1);
              nextSubtitle.trimmedData.start = value;
            }

            const editableSubtitle = structuredClone(itemSubtitlesToEdit[subtitleIndex]);
            editableSubtitle.data.end += (value - editableSubtitle.trimmedData.end) * (editableSubtitle?.playbackRate ?? 1);
            editableSubtitle.trimmedData.end = value;
            startValue = editableSubtitle.trimmedData.start;
            updatedSubtitles = itemSubtitlesToEdit.map((subtitle, subIndex) => {
              if (subIndex === subtitleIndex) {
                return editableSubtitle;
              } else if (subIndex === subtitleIndex + 1) {
                return nextSubtitle;
              } else {
                return subtitle;
              }
            });
            actionToBuffer.undoStartValue = actionToBuffer.redoStartValue = startValue;
          }
          break;
        }
        case 'text': {
          let startValue;

          updatedSubtitles = structuredClone(itemSubtitlesToEdit).map(item => {
            if (id === item.key) {
              const updatedValueData = item.trimmedData;
              updatedValueData[key] = value;
              startValue = updatedValueData.start;
              return { ...item, data: { ...item.data, text: value }, trimmedData: updatedValueData };
            } else {
              return item;
            }
          });

          actionToBuffer.undoStartValue = actionToBuffer.redoStartValue = startValue;
          break;
        }
        case 'add': {
          if (isSubtitlesEmpty) {
            const { trimStart, trimEnd, trimmedDuration, playbackRate = 1 } = video[mode === CLIPS_TYPE ? 'scenes' : 'voiceoverSegments']?.find(item => item.id === keyId);
            const start = timeStampToMs(trimStart);
            const end = timeStampToMs(trimEnd);
            const trimmedDurationWithPlayback = Math.floor(trimmedDuration / playbackRate);
            const newSubtitle = {
              key: uuidv4(),
              [keyName]: keyId,
              playbackRate,
              type: 'cue',
              data: {
                start: start,
                end: trimmedDuration > EMPTY_SUBTITLE_LENGTH * playbackRate ? start + EMPTY_SUBTITLE_LENGTH * playbackRate : end,
                text: '',
              },
              trimmedData: {
                text: '',
                start: sceneOrSegmentInfo.limits.start,
                end:
                  sceneOrSegmentInfo.limits.start +
                  (trimmedDurationWithPlayback > EMPTY_SUBTITLE_LENGTH ? EMPTY_SUBTITLE_LENGTH : trimmedDurationWithPlayback),
              },
            };

            // For clips subtitles check overlapping by voiceover
            if (mode === CLIPS_TYPE) {
              const itemOverlappedBy = getItemOverlappedBy(newSubtitle.trimmedData, sceneOrSegmentInfo.overlappedBy);
              if (itemOverlappedBy?.length) {
                switch (itemOverlappedBy[0].coverage) {
                  case VOICEOVER_COVERAGE_TYPES.START:
                    newSubtitle.trimmedData.start = itemOverlappedBy[0].end;
                    newSubtitle.data.start = newSubtitle.trimmedData.start - start;
                    break;
                  case VOICEOVER_COVERAGE_TYPES.END:
                    newSubtitle.trimmedData.end = itemOverlappedBy[0].start;
                    newSubtitle.data.end = start + (newSubtitle.trimmedData.end - newSubtitle.trimmedData.start) * sceneOrSegmentInfo?.limits?.playbackRate;
                    break;
                  case VOICEOVER_COVERAGE_TYPES.INTERMEDIATE:
                    if (itemOverlappedBy[0].start - newSubtitle.trimmedData.start >= TIME_CORRECTION_STEP) {
                      newSubtitle.trimmedData.end = itemOverlappedBy[0].start;
                      newSubtitle.data.end = start + (newSubtitle.trimmedData.end - newSubtitle.trimmedData.start) * sceneOrSegmentInfo?.limits?.playbackRate;
                    } else {
                      newSubtitle.trimmedData.start = itemOverlappedBy[0].end;
                      newSubtitle.data.start = newSubtitle.trimmedData.start - start;
                      newSubtitle.trimmedData.end = Math.min(newSubtitle.trimmedData.start + EMPTY_SUBTITLE_LENGTH, sceneOrSegmentInfo?.limits?.end);
                      newSubtitle.data.end = start + (newSubtitle.trimmedData.end) * sceneOrSegmentInfo?.limits?.playbackRate;
                    }
                    break;
                  default: break;
                }
                if (newSubtitle.data.start > newSubtitle.data.end) {
                  newSubtitle.data.end = trimmedDuration > EMPTY_SUBTITLE_LENGTH * playbackRate ? newSubtitle.data.start + EMPTY_SUBTITLE_LENGTH * playbackRate : trimEnd;
                  newSubtitle.trimmedData.end = sceneOrSegmentInfo.limits.start +
                    (trimmedDurationWithPlayback > EMPTY_SUBTITLE_LENGTH ? EMPTY_SUBTITLE_LENGTH : trimmedDurationWithPlayback);
                }

              }
            }

            updatedSubtitles.push(newSubtitle);
            setHasChanged(true);
            break;
          }

          //  For new subs before the first one
          let undoStartValue = prevSubtitle?.trimmedData?.start;
          let redoStartValue = prevSubtitle?.trimmedData?.end;

          // Insert new item before existing subtitles
          const isNewFirstItem = id === null;

          const followingSubtitles = itemSubtitlesToEdit.slice(id === null ? 0 : subtitleIndex + 1).filter(item => item.hasOwnProperty('trimmedData'));
          // Doesn't exists in case of isNewFirstItem, the case handles doesn't use this variable
          const prevSubtitle = id === null ? null : itemSubtitlesToEdit[subtitleIndex];
          // Doesn't exist in case of isLastSubtitle, the case handles doesn't use this variable
          let nextSubtitle = null;
          if (followingSubtitles.length > 0) {
            nextSubtitle = itemSubtitlesToEdit[isNewFirstItem ? subtitleIndex : subtitleIndex + 1];
          }

          // Insert new item as last for the scene
          const isLastSubtitle =
            followingSubtitles.length === 0 &&
            filteredByTrimmedData[filteredByTrimmedData.length - 1].trimmedData?.end + EMPTY_SUBTITLE_LENGTH >= sceneOrSegmentInfo?.limits?.end;

          const currentSceneOrSegment = video[sceneId ? 'scenes' : 'voiceoverSegments'].find(item => item.id === keyId);
          const trimStart = timeStampToMs(currentSceneOrSegment.trimStart);
          const trimEnd = timeStampToMs(currentSceneOrSegment.trimEnd);

          const data = {
            text: '',
            start: prevSubtitle && prevSubtitle[keyName] === sceneOrSegmentInfo?.limits[keyName] ? prevSubtitle?.data.end : trimStart,
          };
          const trimmedData = { text: '', start: prevSubtitle?.trimmedData?.end };


          // Handle edge cases inserting the new subtitle
          if (isNewFirstItem && nextSubtitle) {
            const { trimmedData: nextTrimmedData, data: nextData } = nextSubtitle;
            undoStartValue = nextTrimmedData.start;
            data.start = Math.max(trimStart, nextData.start / sceneOrSegmentInfo?.limits?.playbackRate - EMPTY_SUBTITLE_LENGTH);
            data.end = nextData.start;
            trimmedData.start = Math.max(sceneOrSegmentInfo?.limits?.start, nextTrimmedData.start - EMPTY_SUBTITLE_LENGTH);
            trimmedData.end = nextTrimmedData.start;
          } else if (isLastSubtitle) {
            const { data: prevData } = prevSubtitle;
            data.end = Math.min(prevData.end + EMPTY_SUBTITLE_LENGTH * sceneOrSegmentInfo?.limits?.playbackRate, trimEnd);
            trimmedData.end = sceneOrSegmentInfo?.limits?.end;
          } else {
            if (nextSubtitle) {
              data.end = nextSubtitle.data.start;
              trimmedData.end = nextSubtitle.trimmedData.start;
            } else {
              data.end = Math.min(data.start + EMPTY_SUBTITLE_LENGTH * sceneOrSegmentInfo?.limits?.playbackRate, trimEnd);
              trimmedData.end = Math.min(trimmedData.start + EMPTY_SUBTITLE_LENGTH, sceneOrSegmentInfo?.limits?.end);
            }
          }

          // New subtitle item with parameters
          const newSubtitle = {
            ...(isNewFirstItem ? nextSubtitle : prevSubtitle),
            key: uuidv4(),
            data,
            trimmedData,
            [keyName]: sceneOrSegmentInfo?.limits[keyName],
            playbackRate: sceneOrSegmentInfo?.limits?.playbackRate ?? 1,
          };

          if (mode === CLIPS_TYPE) {
            const itemOverlappedBy = getItemOverlappedBy(newSubtitle.trimmedData, sceneOrSegmentInfo.overlappedBy);

            if (itemOverlappedBy?.length) {
              switch (itemOverlappedBy[0].coverage) {
                case VOICEOVER_COVERAGE_TYPES.START:
                  newSubtitle.trimmedData.start = itemOverlappedBy[0].end;
                  newSubtitle.data.start = newSubtitle.trimmedData.start - sceneOrSegmentInfo?.limits?.start;
                  break;
                case VOICEOVER_COVERAGE_TYPES.INTERMEDIATE:
                  if (itemOverlappedBy[0].start - newSubtitle.trimmedData.start >= TIME_CORRECTION_STEP) {
                    newSubtitle.trimmedData.end = itemOverlappedBy[0].start;
                    newSubtitle.data.end = newSubtitle.trimmedData.end * sceneOrSegmentInfo?.limits?.playbackRate - sceneOrSegmentInfo?.limits?.start;
                  } else {
                    newSubtitle.trimmedData.start = itemOverlappedBy[0].end;
                    newSubtitle.data.start = newSubtitle.trimmedData.start - sceneOrSegmentInfo?.limits?.start;
                    newSubtitle
                  }
                  break;
                case VOICEOVER_COVERAGE_TYPES.END:
                  newSubtitle.trimmedData.end = itemOverlappedBy[0].start;
                  newSubtitle.data.end = (newSubtitle.trimmedData.end - newSubtitle.trimmedData.start) * sceneOrSegmentInfo?.limits?.playbackRate - sceneOrSegmentInfo?.limits?.start;
                  break;
                case VOICEOVER_COVERAGE_TYPES.FULL:
                  newSubtitle.trimmedData.start = itemOverlappedBy[0].end;
                  newSubtitle.data.start = newSubtitle.trimmedData.start - sceneOrSegmentInfo?.limits?.start;
                  newSubtitle.trimmedData.end = Math.min(newSubtitle.trimmedData.start + EMPTY_SUBTITLE_LENGTH, sceneOrSegmentInfo?.limits?.end);
                  newSubtitle.data.end = newSubtitle.data.start + (newSubtitle.trimmedData.end - newSubtitle.trimmedData.start) * sceneOrSegmentInfo?.limits?.playbackRate;
                default: break;
              }
            }
          }

          updateChangedItems({ [keyName]: keyId, language: currentLanguage.id });

          const indexToSplice = isNewFirstItem ? subtitleIndex : subtitleIndex + 1;

          // Updated subtitles for the language with new added item
          updatedSubtitles.push(...itemSubtitlesToEdit.toSpliced(indexToSplice, 0, newSubtitle));

          actionToBuffer.undoStartValue = undoStartValue;
          actionToBuffer.redoStartValue = redoStartValue;

          const currentSceneSubtitlesIndex = isNewFirstItem ? 0 : subtitleIndex + 1;

          setTimeout(() => {
            setActiveSubtitle(updatedSubtitles[currentSceneSubtitlesIndex]);
          }, 50);

          break;
        }
        case 'merge': {
          let startValue;

          if (subtitleIndex < itemSubtitlesToEdit.length - 1) {
            const prevSubtitle = itemSubtitlesToEdit[subtitleIndex];
            const nextSubtitle = itemSubtitlesToEdit[subtitleIndex + 1];
            startValue = prevSubtitle.trimmedData.start;

            const newSubtitle = {
              ...prevSubtitle,
              data: {
                start: prevSubtitle.data.start,
                end: nextSubtitle.data.end,
                text: prevSubtitle.data.text ? `${prevSubtitle.data.text} ${nextSubtitle.data.text}` : nextSubtitle.data.text,
              },
              trimmedData: {
                text: prevSubtitle.trimmedData.text
                  ? `${prevSubtitle.trimmedData.text} ${nextSubtitle.trimmedData.text}`
                  : nextSubtitle.trimmedData.text,
                start: prevSubtitle.trimmedData.start,
                end: nextSubtitle.trimmedData.end,
              },
            };

            updatedSubtitles.push(...itemSubtitlesToEdit.toSpliced(subtitleIndex, 2, newSubtitle));

            actionToBuffer.undoStartValue = actionToBuffer.redoStartValue = startValue;
          }
          break;
        }
        case 'delete': {
          actionToBuffer.undoStartValue = itemSubtitlesToEdit[subtitleIndex].trimmedData.start;
          // For redo deletion, we need to find the one closest subtitle
          actionToBuffer.redoStartValue =
            itemSubtitlesToEdit[subtitleIndex - 1]?.trimmedData?.start || itemSubtitlesToEdit[subtitleIndex + 1]?.trimmedData?.start;

          const finalSubtitles = itemSubtitlesToEdit.filter(item => id !== item.key);

          updatedSubtitles.push(...finalSubtitles);
          break;
        }
        default:
          break;
      }
      newGeneralAction({ ...actionToBuffer, updatedSceneSubtitles: updatedSubtitles });

      setSubtitles(prevState => ({
        ...prevState,
        [currentLanguage.id]: {
          ...prevState[currentLanguage.id],
          [mode]: prevState[currentLanguage.id][mode].map(item =>
            item[keyName] === keyId ? { ...item, subtitles: updatedSubtitles } : item
          ),
        }
      }));
    },
    [subtitles, currentLanguage, video]
  );

  //Remove when legacy web trimmed videos are fully deprecated
  const extractSceneVideo = videoDetails => {
    return videoDetails.scenes.map(({ orientation, dimensions }) => ({ orientation, dimensions }));
  };

  const updateVideoData = async videoDetails => {
    const scenesData = extractSceneVideo(videoDetails);
    const createDate = format(new Date(Date.parse(`${videoDetails.createDate}`)), 'dd MMM yyyy');
    const updateDate = format(new Date(Date.parse(`${videoDetails.updateDate}`)), 'dd MMM yyyy');

    const hlsVideo = ResourceHelper.getResourceByLabel(videoDetails, 'hls')?.url;
    if (hlsVideo) {
      videoDetails.video = `${hlsVideo}?${Date.now()}`;
    }
    const scenes = [...videoDetails.scenes];
    scenes.sort((a, b) => a.sort - b.sort);
    setVideo({ ...videoDetails, scenesData, createDate, updateDate, scenes });

    try {
      const response = await VideoService.getStoryChapters(videoId);
      setVideoChapters(response.chapters.filter(item => item.startTime <= videoDetails.duration));
    } catch (e) {
      console.log('Error while requesting chapters', e);
    }
  };

  // Add scene to changed scenes array with language id
  const updateChangedItems = ({ sceneId, segmentId, language }) => {
    setChangedItems(prevState => {
      const type = sceneId ? CLIPS_TYPE : VOICEOVER_TYPE;
      const value = sceneId ?? segmentId;
      if (prevState.hasOwnProperty(language)) {
        if (!prevState[language][type]) {
          return { ...prevState, [language]: { ...prevState[language], [type]: [value] } };
        } else {
          if (prevState[language][type]?.indexOf(value) < 0) {
            return {
              ...prevState,
              [language]: { ...prevState[language], [type]: [...prevState[language][type], value] },
            };
          }
          return prevState;
        }
      } else {
        return { ...prevState, [language]: { [type]: [value] } };
      }
    });
  };

  // Join parsed SRTs arrays into one with recalculating timestamps of start/end values of none 1st scene
  const joinClipsSubtitles = (parsedSrts, audioSegments = []) => {
    let maxTimestamp = introDuration;
    const processedSubs = parsedSrts.map((item, sceneIndex) => {
      const limits = scenesLimits.find(item => item.sceneId === video.scenes[sceneIndex].id);
      // Find voiceover segments that overlaps the scene
      const overlappedBy = getItemOverlappedBy(limits, audioSegments);
      if (item?.subtitles?.some(item => item.hasOwnProperty('trimmedData'))) {
        const lastVisibleElement = [...item.subtitles].reverse().find(item => item.hasOwnProperty('trimmedData'));
        const lastVisibleIndex = item.subtitles.indexOf(lastVisibleElement);
        return {
          ...item,
          limits,
          overlappedBy,
          subtitles: item.subtitles.map((item, index) => {
            if (item.hasOwnProperty('trimmedData')) {
              let start = item.trimmedData.start;
              let end = item.trimmedData.end;
              if (maxTimestamp > 0) {
                start += maxTimestamp;
                end += maxTimestamp;
              }
              if (index === lastVisibleIndex) {
                maxTimestamp += video.scenes[sceneIndex].trimmedDuration / (item?.playbackRate ?? 1);
              }
              return {
                ...item, key: uuidv4(), trimmedData: { ...item.trimmedData, start, end }
              };
            } else {
              return item;
            }
          }),
        };
      } else {
        maxTimestamp += video.scenes[sceneIndex].trimmedDuration / (video.scenes[sceneIndex]?.playbackRate ?? 1);
        return { ...item, limits, subtitles: item.subtitles, overlappedBy };
      }
    });
    return processedSubs;
  };


  const joinSegmentsSubtitles = (parsedSrts, voiceoverSegments = []) => {
    return parsedSrts.map((item, segmentIndex) => {
      const limits = {
        start: voiceoverSegments[segmentIndex].start,
        end: voiceoverSegments[segmentIndex].end,
        segmentId: voiceoverSegments[segmentIndex].id,
        playbackRate: 1,
      }
      if (item?.subtitles?.some(item => item.hasOwnProperty('trimmedData'))) {
        return {
          ...item,
          limits,
          subtitles: item.subtitles.map(subtitle =>
            subtitle?.trimmedData ?
              {
                ...subtitle,
                key: uuidv4(),
                trimmedData: {
                  ...subtitle.trimmedData,
                  start: subtitle.trimmedData.start + limits.start,
                  end: subtitle.trimmedData.end + limits.start,
                },
                limits
              } : { ...subtitle, limits })
        };
      } else {
        return { ...item, limits, subtitles: item.subtitles };
      }

    });
  }

  const isEmpty = object => {
    if (object) {
      return Object.keys(object).length === 0;
    }
    return true;
  };

  /**
   * 
   * @param {*} item scene or segment
   * @param {*} type 
   */
  const getSrtsArrayByLanguage = async (type = CLIPS_TYPE, scenesOrSegments, language = null) => {
    const keyName = type === CLIPS_TYPE ? 'sceneId' : 'segmentId';
    const srts = await Promise.all(scenesOrSegments.map(async ({ id, subtitlesMeta, trimEnd, trimStart, sort = 0, playbackRate = 1 }) => {
      let subs = {};
      let hasEnhancedSubs = false;
      if (subtitlesMeta) {
        const languageSubs = subtitlesMeta?.filter(item => (language ? language.includes(item.language) : Boolean(item.original)));
        if (!languageSubs.length) {
          if (language !== null) {
            return null;
          }
          return { [keyName]: id, sort, subtitles: [] };
        }
        if (languageSubs.length > 1) {
          const enhancedSubs = languageSubs.find(item => item.enhanced && !Boolean(item.trimmed))
          if (!hasEnhancedSubs && enhancedSubs) {
            hasEnhancedSubs = true;
          }
          subs = enhancedSubs || languageSubs.find(item => !Boolean(item.trimmed));
        } else {
          subs = languageSubs[0];
        }
      }

      let fullSrt = '';
      if (subs && !isEmpty(subs)) {
        const response = await fetch(subs.srtPath);
        fullSrt = await response.text();
      }

      const recalculatedToTrimmed = trimSubsWithOffsets({ id, trimEnd, trimStart, sort, playbackRate }, keyName, fullSrt);
      // For translated subtitles define the original language text and put it into data
      if (language !== null && subtitles[subtitlesLanguage.id] && subtitles[subtitlesLanguage.id][type]) {
        const translatedSubtitles = subtitles[subtitlesLanguage.id][type]?.find(item => item[keyName] === id)?.subtitles;
        recalculatedToTrimmed.forEach((item, index) => {
          item.data.originalText = translatedSubtitles[index]?.data?.text;
        });
      }
      return { [keyName]: id, sort, subtitles: recalculatedToTrimmed, isGenerated: subtitlesMeta.length !== 0, enhanced: hasEnhancedSubs };
    })
    );
    return srts;
  }

  // Generate subtitles array from scenes array by defined language. If language is not set - original language subs will be generated
  const generateSubtitlesFromSrtArray = async (scenes, voiceoverSegments, language = null) => {
    let voiceover = null;
    let clips = null;

    if (appConfigLanguages.find(item => item.id === language) || !language) {
      if (voiceoverSegments?.length > 0) {
        const trimmedSegmentsSrts = await getSrtsArrayByLanguage(VOICEOVER_TYPE, voiceoverSegments, language);
        if (!trimmedSegmentsSrts.includes(undefined) && trimmedSegmentsSrts.some(item => Boolean(item))) {
          // join voiceover subtitles
          voiceover = joinSegmentsSubtitles(trimmedSegmentsSrts, voiceoverSegments);
        }
      }

      if (scenes.length > 0) {
        const trimmedScenesSrts = await getSrtsArrayByLanguage(CLIPS_TYPE, scenes, language);
        if (!trimmedScenesSrts.includes(undefined)) {
          if (trimmedScenesSrts.some(item => Boolean(item))) {
            clips = joinClipsSubtitles(trimmedScenesSrts.filter(item => item !== null), voiceoverSegments);
          }
        }
      }
      if (!clips && !voiceover) {
        return null;
      }
      return { clips, voiceover };
    }
  };

  // Generate transcription from scenes with all defined languages
  const generateTranslation = async (scenes, voiceoverSegments) => {
    const translationsObject = {};
    const translationLanguages = appConfigLanguages.filter(item => item.id !== subtitlesLanguage?.id);
    await Promise.all(
      translationLanguages.map(async item => {
        const subs = await generateSubtitlesFromSrtArray(scenes, voiceoverSegments, item.id);
        if (subs) {
          translationsObject[item.id] = subs;
        }
      })
    );
    // Check is all subtitles has same length, delete one that has different length
    let isTranslationMissing = true;
    for (const language in translationsObject) {
      for (const type in translationsObject[language]) {
        if (translationsObject[language][type]?.length === 0) {
          delete translationsObject[language][type];
        } else {
          // Check scene by scene if subtitles has same length
          if (translationsObject[language][type]?.length === subtitles[subtitlesLanguage?.id][type]?.length) {
            // There is at least one translation with the same length
            isTranslationMissing = false;
          }
        }
      }
    }

    if (isTranslationMissing) {
      setIsMissing(prevState => ({ ...prevState, [TRANSLATION]: true }));
    }
    // If subtitles exists add them to subtitles object and set translation mode
    if (Object.keys(translationsObject).length > 0) {
      setSubtitles(prevState => ({ ...prevState, ...translationsObject }));
      setMode(EDITOR_MODE.TRANSLATE_SUBTITLES_MODE);
    }
  };

  const getVoiceoverData = async (video) => {
    try {
      const audioTracks = await VormatsAPI.getStoryVoiceoverTrack(video.id);
      const { audioSegments } = audioTracks;
      return audioSegments?.sort((a, b) => a.storyOffset - b.storyOffset).filter(item => item.storyOffset < video.duration).map((segment, index) => {
        const trimStart = segment.trimStartOffset;
        const start = segment.storyOffset + trimStart;
        let trimEnd = segment.trimEndOffset;
        let trimmedDuration = trimEnd - trimStart;
        let end = segment.storyOffset + trimEnd;
        if (end > video.duration) {
          end = video.duration;
          trimmedDuration = end - start;
          trimEnd = trimStart + trimmedDuration;
        }
        return { id: segment.id, sort: index, start, end, playbackRate: 1, trimStart, trimEnd, trimmedDuration, subtitlesMeta: segment.subtitles };
      })
    } catch (error) {
      if (error.response?.status === 500) {
        throw error;
      }
      return [];
    }
  }

  const handleSuccessfulSubtitleGeneration = async (videoId) => {
    const videoDetails = await VideoService.getVideoDetails(videoId);
    const voiceoverSegments = await getVoiceoverData(videoDetails);
    updateVideoData({ ...videoDetails, voiceoverSegments });
    !voiceoverSegments.length && setListMode(CLIPS_TYPE);
    const sortedScenes = [...videoDetails.scenes].sort((a, b) => a.sort - b.sort);
    const scenesHasSubtitles = sortedScenes.filter(item => item.subtitles);
    if (scenesHasSubtitles.length > 0) {
      if (scenesHasSubtitles.length < sortedScenes.length || voiceoverSegments.length > voiceoverSegments.filter(item => item.subtitlesMeta.length).length) {
        setIsMissing(prevState => ({ ...prevState, [TRANSCRIPTION]: true }));
      }
      const subs = await generateSubtitlesFromSrtArray(sortedScenes, voiceoverSegments);
      if (Object.keys(subs).length) {
        setSubtitles(prevState => ({ ...prevState, [currentLanguage.id]: subs }));
        setLoading(null);
      }
      setMode(EDITOR_MODE.EDIT_SUBTITLES_MODE);
    }
  };

  // Check subtitles status until failed or succeeded status
  const checkUntilFinish = async (estimatedTime = 0, callback = () => { }, tries = 0) => {
    try {
      let { status } = await CmsAPI.getSubtitleStatus(videoId);
      console.log('got new status', status);
      if (status && status === 404 && tries <= 100) {
        console.log(`404, probing again in ${3 * tries}s`);
        setTimeout(function () {
          console.log('404 try again checkUntilFinish');
          checkUntilFinish(0, callback, tries + 1);
        }, 5000);
        return;
      }
      if (status.toLowerCase() === 'running') {
        console.log('estimatedTime in running', estimatedTime);
        setLoading(transcribeLoadingText);
        let timeout = estimatedTime === 0 ? 5 : estimatedTime;
        console.log('---estimatedTime', estimatedTime);
        console.log(`Not done yet, probing again in ${timeout}s`);
        setTimeout(function () {
          setEstimatedTime(prevState => (timeout > prevState ? 0 : prevState - timeout));
          const nextEstimatedTime = Math.max(0, estimatedTime - timeout);
          checkUntilFinish(nextEstimatedTime, callback, tries + 1);
        }, timeout * 1000);
      }

      // Succeeded
      if (status.toLowerCase() == 'succeeded') {
        console.log('Succeeded');
        // Update video object
        await handleSuccessfulSubtitleGeneration(videoId);
        return;
      }
      if (status.toLowerCase() == 'failed') {
        setLoading(null);
        setError(true);
        return;
      }
      if (status.toLowerCase() == 'aborted') {
        setLoading(null);
        return;
      }
    } catch (e) {
      console.log('Error in checkUntilFinish', e);
      if (e.response?.status) {
        if (init) {
          setError(true);
        }
        setLoading(null);
        return;
      }
    }
  };

  // Define subtitles original language
  const defineLanguage = scenes => {
    const originalLanguages = scenes
      .map(scene => scene.subtitlesMeta?.find(item => item?.original === 1))
      .map(item => item?.language)
      .filter(item => Boolean(item));
    const unique = [...new Set(originalLanguages)];
    const language = unique[0];
    const definedLanguage = supportedLanguages.find(item => item.id.includes(language));
    return definedLanguage;
  };

  const onSeekToSubtile = useCallback((data, timestamp = true) => {
    if (data) {
      if (timestamp && data?.start) {
        setSeekToSubtitleValue((data?.sceneLimits?.min ?? data.start + 50) / 1000);
        return;
      }
      let item = null;
      for (const type in selectedLanguageSubtitles) {
        if (!item) {
          item = selectedLanguageSubtitles[type].find(({ subtitles }) => subtitles.find(item => item.key === data?.key));
        }
      }
      const index = item.subtitles.findIndex(item => item.key === data?.key);
      if (data?.hasOwnProperty('start') && index >= 0) {
        const seekToValue = item?.subtitles[index]?.trimmedData?.start;
        setSeekToSubtitleValue((seekToValue + 50) / 1000);
      }
    }
  }, [selectedLanguageSubtitles]);

  const getTranslationsArray = (scenesOrSegments) => {
    return scenesOrSegments.map(item => {
      const originalLanguage = item.subtitlesMeta.find(subtitle => subtitle.original)?.language;
      if (originalLanguage) {
        const isEnglishGB = originalLanguage === 'en_GB';
        const languages = item.subtitlesMeta.map(subtitle => subtitle.language);
        const unique = [...new Set(languages)];
        const translateLanguages = appConfigLanguages
          .filter(language => !unique.find(uniqueLanguage => uniqueLanguage === language.id))
          .map(language => language.id)
          .filter(language => (isEnglishGB ? language !== 'en_US' : true));
        if (translateLanguages.length === 0) {
          translateLanguages.push(...appConfigLanguages.map(item => item.id).filter(item => (isEnglishGB ? item !== 'en_US' : true)));
        }
        return {
          id: item.id,
          from: originalLanguage,
          to: translateLanguages.filter(item => item !== originalLanguage),
        };
      }
    });
  }

  // Get translation for all user allowed languages
  const getTranslation = async () => {
    setLoading(translateLoadingText);
    if (isMissing[TRANSLATION]) {
      setIsMissing(prevState => ({ ...prevState, [TRANSLATION]: false }));
    }
    const scenes = getTranslationsArray(video.scenes);
    const segments = getTranslationsArray(video.voiceoverSegments);

    const { result: translatedSubtitles } = (await CmsAPI.translateSubtitles(video.id, { scenes, segments })) || {};

    if (translatedSubtitles?.en_US) {
      const language = supportedLanguages.find(({ id }) => id === 'en_US');
      setCurrentLanguage(language);
    }

    let videoDetails = await VideoService.getVideoDetails(videoId);
    const voiceoverSegments = await getVoiceoverData(videoDetails);
    updateVideoData({ ...videoDetails, voiceoverSegments });
    await generateTranslation([...videoDetails.scenes].sort((a, b) => a.sort - b.sort), voiceoverSegments);
    setMode(EDITOR_MODE.TRANSLATE_SUBTITLES_MODE);
    setLoading(null);
  };

  const trimSubsWithOffsets = (scene, keyName, srt) => {
    const { id, trimStart, trimEnd, playbackRate = 1 } = scene;
    const sceneTrimStart = timeStampToMs(trimStart);
    const sceneTrimEnd = timeStampToMs(trimEnd);
    let parsedFullSrt = [];
    try {

      parsedFullSrt = parseSync(srt);
    } catch (e) {
      console.log('Error while parsing srt', e);
    }
    const trimmedSubtitles = parsedFullSrt.map(item => {
      if (
        item.data.end < sceneTrimStart ||
        item.data.end < sceneTrimStart + TIME_CORRECTION_STEP ||
        item.data.start > sceneTrimEnd ||
        item.data.start > sceneTrimEnd - TIME_CORRECTION_STEP
      ) {
        return { ...item, playbackRate };
      }

      if (sceneTrimStart >= item.data.start && sceneTrimStart <= item.data.end) {
        const start = item.data.start < sceneTrimStart ? 0 : item.data.start - sceneTrimStart;
        const end = item.data.end - sceneTrimStart;
        return {
          ...item,
          trimmedData: { ...item.data, start: start / playbackRate, end: end / playbackRate },
          playbackRate,
        };
      }

      if (sceneTrimEnd >= item.data.start && sceneTrimEnd <= item.data.end) {
        const start = item.data.start - sceneTrimStart;
        const end = item.data.end > sceneTrimEnd ? sceneTrimEnd - sceneTrimStart : item.data.end - sceneTrimStart;
        return {
          ...item,
          trimmedData: { ...item.data, start: start / playbackRate, end: end / playbackRate },
          playbackRate,
        };
      }

      const start = item.data.start - sceneTrimStart;
      const end = item.data.end - sceneTrimStart;
      return {
        ...item,
        trimmedData: { ...item.data, start: start / playbackRate, end: end / playbackRate },
        playbackRate,
      };
    });

    const result = trimmedSubtitles.map(item => ({ ...item, [keyName]: id }));
    return result;
  };

  const handleTranslate = () => {
    const originalLanguage = video?.subtitlesMeta.find(({ original, enhanced }) => !!original && enhanced) || video?.subtitlesMeta.find(({ original }) => !!original);
    // check if the subtitles were generated automatically and show a confirmation modal if so
    if (originalLanguage?.created === originalLanguage?.updated) {
      setTranslateConfirm(true);
      return;
    }

    getTranslation();
  };

  const handleEnhance = async () => {
    setShowEnhanceConfirmModal(true);
  };

  const handleConfirmEnhance = async () => {
    setShowEnhanceConfirmModal(false);
    setLoading(enhanceLoadingText);
    await VormatsAPI.enhanceSubtitles(video.id);
    await handleSuccessfulSubtitleGeneration(video.id);
    setLoading(null);
  }

  const handleGenerateVoiceover = async () => {
    setShowVoiceModal(true);
  }

  const handleVoiceSelect = async (voiceId) => {
    await VormatsAPI.generateVoiceover(video.id, voiceId);
    history.push(`/eg/video/${video.id}`);
  }

  const confirmTranslate = async () => {
    setTranslateConfirm(false);
    await handleSave();
    getTranslation();
  };

  // Parse scene url to get a video name
  const parseSceneName = url => {
    const splitted = url.split('/');
    const partIncdludesId = splitted.find(item => item.includes('?'));
    const namedPart = partIncdludesId.split('?')[0];
    return namedPart;
  };

  const getFilesListAndEstimatedTime = (sceneOrSegment, forAllScenes) => {
    let filteredItems = structuredClone(sceneOrSegment);
    if (!forAllScenes) {
      const itemsWithoutSubtitles = sceneOrSegment.filter(item => item.subtitlesMeta.length === 0);
      if (itemsWithoutSubtitles.length > 0) {
        filteredItems = itemsWithoutSubtitles;
      }
    }
    const fileNames = filteredItems?.map(item => item.video ? parseSceneName(item.video) : JSON.stringify({ segmentId: item.id }));
    const durationsSum = filteredItems
      .map(item => timeStampToMs(item.trimEnd) - timeStampToMs(item.trimStart))
      .reduce((prev, current) => prev + current);
    const estTime = Math.round(
      (durationsSum / 1000 + (1 + filteredItems.length / TRANSCRIBE_PARALLEL_VIDEOS)) * TRANSCRIBE_ESTIMATION_FACTOR
    );
    return { fileNames, estTime }
  }

  // Generate subtitles for all video scenes
  const handleGenerateTranscription = async (callback = () => { }, forAllScenes = false) => {
    if (isMissing[TRANSCRIPTION]) {
      setIsMissing(prevState => ({ ...prevState, [TRANSCRIPTION]: false }));
    }
    setLoading(transcribeLoadingText);
    const credentials = await Auth.currentUserCredentials().catch(e => console.log(e));

    let estimatedTime = 0;
    const payload = { identityId: credentials.identityId, audioLanguage: subtitlesLanguage.id };

    if (video.voiceoverSegments.length) {
      const { fileNames, estTime } = getFilesListAndEstimatedTime(video.voiceoverSegments, forAllScenes);
      payload.audioFileNames = fileNames;
      estimatedTime += estTime;
    }

    const { fileNames, estTime } = getFilesListAndEstimatedTime(video.scenes, forAllScenes);
    payload.videoFileNames = fileNames;
    estimatedTime += estTime;

    setEstimatedTime(estimatedTime);
    await CmsAPI.generateSubtitles(videoId, payload);
    checkUntilFinish(estTime, callback);
  };

  // Cancel transcription during generation
  const handleCancelTranscription = async () => {
    try {
      await CmsAPI.cancelSubtitles(videoId);
    } catch (e) {
      console.log('Error while canceling', e);
    }
    setMode(EDITOR_MODE.GENERATE_SUBTITLES_MODE);
    setLoading(null);
  };

  const handleRemoveTranscription = async () => {
    setLoading(transcribeLoadingText);
    setInit(false);
    clearStorage();
    await CmsAPI.deleteSubtitles(video.id);
    setSubtitles({});
    setLoading(null);
    setInit(true);
    setMode(EDITOR_MODE.GENERATE_SUBTITLES_MODE);
  };

  const handleRevert = async () => {
    const confirmRevert = confirm(t('subtitles.editor.confirm_revert'));
    const hasTranslation = Object.keys(subtitles).length > 1;
    if (confirmRevert) {
      clearStorage();
      setMode(EDITOR_MODE.GENERATE_SUBTITLES_MODE);
      setLoading(transcribeLoadingText);
      setSubtitles({});
      setChangedItems({});
      setHasChanged(false);
      await CmsAPI.deleteSubtitles(videoId);
      const subsGeneratedCallback = async () => {
        if (hasTranslation) {
          setTimeout(() => {
            getTranslation();
          }, 500);
        }
      };
      await handleGenerateTranscription(subsGeneratedCallback, true);
    }
  };

  // Save all srt changes (original and translated) on backend
  const handleSave = useCallback(async () => {
    const itemsSubtitlesToSave = [];
    console.log('changedItems', changedItems);
    for (const language in changedItems) {
      for (const type in changedItems[language]) {
        changedItems[language][type].forEach(itemId => {
          const keyName = type === CLIPS_TYPE ? 'sceneId' : 'segmentId';
          const languageSubtitles = subtitles[language][type];
          const sceneSubtitles = languageSubtitles.find(sub => sub[keyName] === itemId);
          console.log('languageSubtitles', { languageSubtitles, sceneSubtitles });
          const changedSubs = subtitles[language][type]
            .find(sub => sub[keyName] === itemId)
            ?.subtitles?.map(item =>
              item.trimmedData
                ? {
                  ...item,
                  data: {
                    ...item.data,
                    text: item.trimmedData.text,
                  },
                }
                : item
            );
          const packSrt = stringifySync(changedSubs, { format: 'SRT' });
          const srtObject = { [keyName]: itemId, srt: packSrt, language };
          const sceneOrSegment = video[type === CLIPS_TYPE ? 'scenes' : 'voiceoverSegments'].find(item => item.id === itemId);

          if (!sceneOrSegment.subtitlesMeta.find(item => item.language === language)) {
            const original = sceneOrSegment.subtitlesMeta.find(item => item.enhanced && item.original) || sceneOrSegment.subtitlesMeta.find(item => item.original);
            srtObject.language = original.language;
          }
          itemsSubtitlesToSave.push(srtObject);
        })
      }
    }
    console.log('itemsSubtitlesToSave', itemsSubtitlesToSave);
    try {
      await CmsAPI.saveSubtitles(video.id, itemsSubtitlesToSave);
      const videoDetails = await VideoService.getVideoDetails(videoId);
      const voiceoverSegments = await getVoiceoverData(videoDetails);
      updateVideoData({ ...videoDetails, voiceoverSegments });
      const scenes = [...videoDetails.scenes].sort((a, b) => a.sort - b.sort);
      const subs = await generateSubtitlesFromSrtArray(scenes, voiceoverSegments);
      setSubtitles({ [subtitlesLanguage.id]: subs });
      setChangedItems({});
      clearStorage();
      await generateTranslation(scenes, voiceoverSegments);
    } catch (e) {
      console.log('Error while saving subs', e);
    } finally {
      setHasChanged(false);
    }
  }, [subtitles, changedItems, getVoiceoverData, updateVideoData, generateSubtitlesFromSrtArray, generateTranslation]);

  const handlePlayerProgress = useCallback(
    value => {
      setPlayedSeconds(value * 1000);
    },
    [video]
  );

  const handleDisableMissing = mode => {
    setIsMissing(prevState => ({ ...prevState, [mode]: false }));
  };

  useEffect(() => {
    if (appConfig) {
      setAppConfigLanguages(appConfig.subtitle_languages);
    }
  }, [appConfig]);

  useEffect(() => {
    const browserLanguage = i18next.language.replace('-', '_');
    let language = supportedLanguages.find(item => item.id.includes(browserLanguage));

    if (!language || language.id === 'en_GB') {
      // Make subtitles default language American English
      language = supportedLanguages.find(item => item.id === 'en_US');
    }
    setSubtitlesLanguage(language);
  }, []);

  useEffect(() => {
    setCurrentLanguage(subtitlesLanguage);
  }, [subtitlesLanguage]);

  useEffect(() => {
    const checkTranslation = async () => {
      setLoading(translateLoadingText);
      const voiceoverSegments = await getVoiceoverData(video);
      await generateTranslation([...video.scenes].sort((a, b) => a.sort - b.sort), voiceoverSegments);
      setLoading(null);
    };

    if (mode === EDITOR_MODE.EDIT_SUBTITLES_MODE) {
      checkTranslation();
    }
  }, [mode]);

  const checkVideo = async () => {
    // Check is video has subtitles.
    const sortedScenes = [...video.scenes].sort((a, b) => a.sort - b.sort);
    const scenesWithSubtitles = sortedScenes.filter(item => Boolean(item.subtitlesMeta.length > 0));
    const voiceoverWithSubtitles = video.voiceoverSegments?.filter(item => item.subtitlesMeta?.length > 0);
    if (scenesWithSubtitles.length > 0 || video.voiceoverSegments.length > 0) {
      if (scenesWithSubtitles.length < sortedScenes.length || voiceoverWithSubtitles.length < video.voiceoverSegments.length) {
        setIsMissing(prevState => ({ ...prevState, [TRANSCRIPTION]: true }));
      }
      // Check original subs language then set setSubtitlesLanguage & setCurrentLanguage with a value
      const definedLanguage = defineLanguage(video.scenes);
      if (definedLanguage) {
        await setSubtitlesLanguage(definedLanguage);
        await setCurrentLanguage(definedLanguage);
        const subs = await generateSubtitlesFromSrtArray(sortedScenes, video.voiceoverSegments);
        if (Object.keys(subs).length > 0) {
          setSubtitles({ [definedLanguage?.id]: subs });
        }
        setMode(EDITOR_MODE.EDIT_SUBTITLES_MODE);
      } else {
        setMode(EDITOR_MODE.GENERATE_SUBTITLES_MODE);
      }
      setLoading(null);
      setInit(true);
    } else {
      // If subtitles DOES NOT exist check subtitles status
      await checkUntilFinish();
      setInit(true);
    }
  };

  useEffect(() => {
    if (video && !init) {
      checkVideo();
    }
  }, [video]);

  useEffect(() => {
    const getVideo = async () => {
      let videoDetails = initVideoDetails;
      try {
        if (!videoDetails) {
          videoDetails = await VideoService.getVideoDetails(videoId);
        }
        const voiceoverSegments = await getVoiceoverData(videoDetails);
        updateVideoData({ ...videoDetails, voiceoverSegments });
        !voiceoverSegments.length && setListMode(CLIPS_TYPE);
      } catch (e) {
        console.log('Get video details error', e);
      }
    };

    if (!video) getVideo();
  }, [videoId]);

  useEffect(() => {
    if (selectedLanguageSubtitles) {

      let activeSub = null;

      if (video?.voiceoverSegments) {
        const currentVoiceover = selectedLanguageSubtitles[VOICEOVER_TYPE]?.find(
          ({ limits }) => playedSeconds >= limits?.start && playedSeconds <= limits?.end
        );
        activeSub = currentVoiceover?.subtitles?.find(
          subItem =>
            subItem.hasOwnProperty('trimmedData') &&
            playedSeconds >= subItem.trimmedData.start &&
            playedSeconds <= subItem.trimmedData.end
        );
      }


      if (!activeSub) {
        const currentScene = selectedLanguageSubtitles[CLIPS_TYPE]?.find(
          ({ limits }) => playedSeconds >= limits?.start && playedSeconds <= limits?.end
        );
        const subtitle = currentScene?.subtitles?.find(
          subItem =>
            subItem.hasOwnProperty('trimmedData') &&
            playedSeconds >= subItem.trimmedData.start &&
            playedSeconds <= subItem.trimmedData.end
        );
        if (subtitle && video?.voiceoverSegments) {
          const itemCoverage = getItemOverlappedBy(subtitle.trimmedData, currentScene?.overlappedBy);
          if (!checkForFullCoverage(itemCoverage)) {
            activeSub = subtitle;
          }
        } else {
          activeSub = subtitle;
        }
      }
      setActiveSubtitle(activeSub);
    }
  }, [playedSeconds, selectedLanguageSubtitles]);

  useEffect(() => {
    if (error) {
      ToastNotification({
        message: `${t('general.toast.failed_transcription')}`,
        isMobileView: isMobileView,
      });
      setError(false);
    }
  }, [error]);

  useEffect(() => {
    if (estimatedTime > 0) {
      setTimeout(() => {
        setEstimatedTime(prevState => prevState - 1);
      }, 1000);
    }
  }, [estimatedTime]);

  useEffect(() => {
    if (videoConfig && videoConfig.ctas?.length && video?.duration) {
      const currentActions = getCurrentActions(videoConfig.ctas, video.duration);

      updateCallActions(currentActions);
      updateInitialCallActions(currentActions);
    }
  }, [videoConfig, video])

  const onReloadEvent = event => {
    const e = event || window.event;
    // Cancel the event
    e.preventDefault();
    if (e) {
      e.returnValue = ''; // Legacy method for cross browser support
    }
    return ''; // Legacy method for cross browser support
  };

  useEffect(() => {
    if (hasChanged) {
      window.addEventListener('beforeunload', onReloadEvent);
    } else {
      window.removeEventListener('beforeunload', onReloadEvent);
    }
    return () => {
      window.removeEventListener('beforeunload', onReloadEvent);
    };
  }, [hasChanged]);

  useEffect(() => {
    let unblock = hasChanged
      ? history.block(tx => {
        // Navigation was blocked! Let's show a confirmation dialog
        // so the user can decide if they actually want to navigate
        // away and discard changes they've made in the current page.
        if (confirm(t('subtitles.editor.user_warning'))) {
          // Unblock the navigation.
          return true;
        } else {
          return false;
        }
      })
      : history.block(() => {
        return true;
      });

    return () => unblock();
  }, [hasChanged]);

  return (
    <div style={{ marginLeft: miniSidebar ? '80px' : '216px' }} className={styles.container}>
      <div className={styles.playerWrapper}>
        {video && (
          <>
            <SubtitlesPlayer
              video={video}
              subtitles={subtitles}
              handlePlayerProgress={handlePlayerProgress}
              seekToSubtitleValue={seekToSubtitleValue}
              videoChapters={videoChapters}
              text={activeSubtitle?.trimmedData?.text}
              videoConfig={videoConfig}
            />
            {mode !== EDITOR_MODE.GENERATE_SUBTITLES_MODE && (
              <EditSubtitleButtons
                mode={mode}
                onEnhance={handleEnhance}
                onGenerateVoiceover={handleGenerateVoiceover}
                onTranslate={handleTranslate}
                onRevert={handleRevert}
                onRemove={handleRemoveTranscription}
                hasChanged={hasChanged}
                isMissing={isMissing}
                handleDisableMissing={handleDisableMissing}
                handleGenerateTranscription={handleGenerateTranscription}
                handleTranslate={getTranslation}
                loading={loading}
                isOnTranslation={subtitlesLanguage.id !== currentLanguage.id}
              />
            )}
          </>
        )}
      </div>
      <SubtitlesRightSidebar
        supportedLanguages={supportedLanguages}
        init={init}
        mode={mode}
        video={video}
        appConfigLanguages={appConfigLanguages}
        subtitles={selectedLanguageSubtitles}
        availableLanguages={availableLanguages}
        subtitlesLanguage={subtitlesLanguageWithEnhanced(subtitlesLanguage)}
        setSubtitlesLanguage={setSubtitlesLanguage}
        loading={loading}
        updateSubtitle={handleUpdateSubtitles}
        activeSubtitle={activeSubtitle}
        handleSeekToSubtitle={onSeekToSubtile}
        hasChanged={hasChanged}
        handleGenerateTranscription={handleGenerateTranscription}
        handleCancelTranscription={handleCancelTranscription}
        currentLanguage={subtitlesLanguageWithEnhanced(currentLanguage)}
        onLanguageChange={setCurrentLanguage}
        estimatedTime={estimatedTime}
        handleSave={handleSave}
        undoLastAction={undoLastAction}
        redoLastAction={redoLastAction}
        controlsDisabled={controlsDisabled}
        scenesLimits={scenesLimits}
        listMode={listMode}
        setListMode={setListMode}
      />
      <SaveSubtitlesModal open={translateConfirm} onClose={() => setTranslateConfirm(false)} onConfirm={confirmTranslate} />
      <VoiceSelectionModal
        open={showVoiceModal}
        onClose={() => setShowVoiceModal(false)}
        onSelect={handleVoiceSelect}
      />
      <Modal className={styles.warningWrapper} open={showEnhanceConfirmModal} onClose={() => setShowEnhanceConfirmModal(false)}>
        <p className={styles.message}>
          {t('subtitles.editor.improve_subtitles.warning')}
        </p>
        <div className={styles.buttonsContainer}>
          <CustomButton label={t('general.cancel')} variant="secondary" onClick={() => setShowEnhanceConfirmModal(false)} />
          <CustomButton label={t('general.continue')} onClick={handleConfirmEnhance} variant="primary" />
        </div>
      </Modal>
    </div>
  );
};

export default withPermissionCheck(SubtitlesEditorContent);
